import React from 'react';
import { createUseStyles } from 'react-jss';
import useModalEscape from '../../hooks/useModalEscape';
import scrollbar from '../../styles/scrollbar';
import RobotIcon from '../icons/RobotIcon';
import { APP_VERSION } from '../../version';
import { useT } from '../../i18n';

const ROBOT_COLORS = ['#d94f2b', '#2a63a8', '#e0a325', '#2f9e6e'];
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,6,3,0.55)',
    zIndex: 20,
    display: ({ open }) => open ? 'block' : 'none',
  },
  drawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '280px',
    maxWidth: '78vw',
    boxSizing: 'border-box',
    background: '#2a1d13',
    borderRight: '1px solid #6b4622',
    zIndex: 21,
    padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 20px calc(env(safe-area-inset-bottom, 0px) + 20px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    transform: ({ open }) => open ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.25s ease',
    boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.06), 6px 0 30px rgba(0,0,0,0.5)',
    overflowY: 'auto',
    ...scrollbar,
  },
  closeBtn: {
    width: '44px',
    height: '44px',
    alignSelf: 'flex-end',
    background: '#3a2718',
    border: '1px solid #533620',
    borderRadius: '8px',
    color: '#e8d6ae',
    fontSize: '16px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    lineHeight: 1,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
    '&:active': {
      boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.5)',
    },
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingBottom: '14px',
    marginBottom: '4px',
    borderBottom: '1px solid #4a3020',
  },
  marks: {
    display: 'flex',
    gap: '7px',
  },
  mark: {
    width: '20px',
    height: '20px',
  },
  title: {
    color: '#f5e6c8',
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    margin: 0,
  },
  modeBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '52px',
    padding: '9px 12px',
    marginTop: '14px',
    background: '#2e1c0d',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 1px 0 rgba(255,255,255,0.07)',
      'inset 0 -2px 0 rgba(0,0,0,0.3)',
    ].join(','),
    '&:active': {
      boxShadow: 'inset 0 0 0 1px #4a3020, inset 0 2px 5px rgba(0,0,0,0.5)',
    },
  },
  modeBarLabel: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#c4a070',
  },
  modeBarChevron: {
    marginLeft: '8px',
    fontSize: '15px',
    lineHeight: 1,
    color: '#c4a070',
  },
  modeBarValue: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: MONO,
    fontSize: '11.5px',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#e0a325',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '14px',
  },
  sectionTitle: {
    fontFamily: MONO,
    color: '#c4a070',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  text: {
    color: '#d9c3a2',
    fontSize: '13.5px',
    lineHeight: 1.55,
    margin: 0,
  },
  plateBtn: {
    minHeight: '46px',
    background: 'none',
    border: '1px solid #6b4622',
    borderRadius: '8px',
    color: '#d99a6a',
    padding: '12px',
    cursor: 'pointer',
    fontSize: '13.5px',
    fontWeight: 700,
    fontFamily: 'inherit',
    marginTop: '8px',
    '&:active': {
      background: 'rgba(217,79,43,0.12)',
    },
  },
});

function HamburgerMenu({ open, onClose, isHost, onResetGame, onOpenShortcuts,
  mode, onOpenSettings }) {
  const classes = useStyles({ open });
  const t = useT();

  useModalEscape(open, onClose);

  return (
    <>
      <div className={classes.overlay} onClick={onClose} />
      <div className={classes.drawer}>
        <button className={classes.closeBtn} onClick={onClose} aria-label={t('a11y.close')}>✕</button>

        <div className={classes.brand}>
          <div className={classes.marks}>
            {ROBOT_COLORS.map(c => (
              <div key={c} className={classes.mark}>
                <RobotIcon color={c} />
              </div>
            ))}
          </div>
          <h2 className={classes.title}>Robotrons</h2>
        </div>

        {onOpenSettings && (
          <button
            type="button"
            className={classes.modeBar}
            onClick={() => { onOpenSettings(); onClose(); }}
            aria-label={`Settings: ${mode || 'normal'}`}
          >
            <span className={classes.modeBarLabel}>{t('game.settings')}</span>
            <span className={classes.modeBarValue}>
              {mode || 'normal'}
              <span className={classes.modeBarChevron} aria-hidden="true">&#8250;</span>
            </span>
          </button>
        )}

        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t('settings.howToPlay')}</div>
          <p className={classes.text}>{t('settings.howToPlayLong')}</p>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t('settings.about')}</div>
          <p className={classes.text}>{t('settings.aboutLine', { version: APP_VERSION })}</p>
        </div>

        {onOpenShortcuts && (
          <button className={classes.plateBtn} onClick={onOpenShortcuts}>
            {t('shortcuts.title')}
          </button>
        )}

        {isHost && (
          <button
            className={classes.plateBtn}
            onClick={() => {
              if (window.confirm(t('settings.resetConfirmLong'))) {
                onResetGame();
                onClose();
              }
            }}
          >
            {t('settings.resetRoom')}
          </button>
        )}
      </div>
    </>
  );
}

export default HamburgerMenu;
